import { produce } from "immer";
import { useEffect, useState } from "react";

export default function useProductEditor(product) {
    const [source, setSource] = useState(undefined);

    useEffect(
        () => {
            if (product) {
                setSource(
                    JSON.parse(
                        JSON.stringify({ data: product.data, metadata: product.metadata }),
                        function (key, value) {
                            if (key === "__typename") return undefined;
                            if (key === "createdAt") return undefined;
                            if (key === "updatedAt") return undefined;
                            if (value === null) return undefined;
                            return value;
                        },
                    )
                );
            } else {
                setSource(
                    {
                        data: {},
                        metadata: {},
                    }
                );
            }
        },
        [product],
    );

    const [raw, setRaw] = useState();

    useEffect(
        () => {
            let x = source;

            if (x?.data?.properties?.length) {
                x = produce(
                    (draft) => {
                        for (const property of draft.data.properties) {
                            property.__id = crypto.randomUUID();
                        }
                    }
                )(x);
            }

            if (x?.data?.components?.length) {
                x = produce(
                    (draft) => {
                        for (const component of draft.data.components) {
                            component.__id = crypto.randomUUID();

                            for (const property of component.properties ?? []) {
                                property.__id = crypto.randomUUID();
                            }
                        }
                    }
                )(x);
            }

            if (x?.data?.layers?.length) {
                x = produce(
                    (draft) => {
                        for (const layer of draft.data.layers) {
                            layer.__id = crypto.randomUUID();

                            for (const property of layer.properties ?? []) {
                                property.__id = crypto.randomUUID();
                            }
                        }
                    }
                )(x);
            }

            if (x?.data?.materials?.length) {
                x = produce(
                    (draft) => {
                        for (const material of draft.data.materials) {
                            material.__id = crypto.randomUUID();

                            for (const property of material.properties ?? []) {
                                property.__id = crypto.randomUUID();
                            }
                        }
                    }
                )(x);
            }

            setRaw(x);
        },
        [source],
    );

    const reader = {
        ...(raw?.data ?? {}),
        ...(raw?.metadata ?? {}),
        id: product?.id,
        createdAt: product?.metadata?.createdAt,
        updatedAt: product?.metadata?.updatedAt,
    };
    if (reader.about === null) reader.about = undefined;
    if (reader.materialName === null) reader.materialName = undefined;
    if (reader.brand?.id) reader.brand = reader.brand.id;
    if (reader.bimClass?.id) reader.bimClass = reader.bimClass.id;
    if (!reader.media) reader.media = [];
    if (!reader.layers) reader.layers = [];
    if (!reader.countries) reader.countries = [];
    if (!reader.tags) reader.tags = [];
    if (!reader.materials) reader.materials = [];
    if (!reader.components) reader.components = [];
    if (!reader.properties) reader.properties = [];

    if (source) {
        reader.suggestedLanguages = [
            source?.data?.name,
            source?.data?.about,
            source?.data?.materialName,
            raw?.data?.name,
            raw?.data?.about,
            raw?.data?.materialName,
        ]
            .filter(Boolean)
            .flat()
            .map((localization) => localization.language)
            .filter((e, i, a) => a.indexOf(e) === i);
    }

    const writer = {
        archived(archived) {
            setRaw(
                produce(
                    (draft) => {
                        draft.metadata.archived = archived;
                    }
                )
            );
        },
        deleted(deleted) {
            setRaw(
                produce(
                    (draft) => {
                        draft.metadata.deleted = deleted;
                    }
                )
            );
        },
        published(published) {
            setRaw(
                produce(
                    (draft) => {
                        draft.metadata.published = published;
                    }
                )
            );
        },
        naturalKey(naturalKey) {
            setRaw(
                produce(
                    (draft) => {
                        draft.metadata.naturalKey = naturalKey;
                    }
                )
            );
        },
        name(name) {
            setRaw(
                produce(
                    (draft) => {
                        draft.data.name = name;
                    }
                )
            );
        },
        about(about) {
            setRaw(
                produce(
                    (draft) => {
                        draft.data.about = about;
                    }
                )
            );
        },
        materialName(materialName) {
            setRaw(
                produce(
                    (draft) => {
                        draft.data.materialName = materialName;
                    }
                )
            );
        },
        tags(tags) {
            setRaw(
                produce(
                    (draft) => {
                        draft.metadata.tags = tags;
                    }
                )
            );
        },
        bimClass(bimClass) {
            setRaw(
                produce(
                    (draft) => {
                        draft.data.bimClass = {
                            id: bimClass.id,
                        };
                    }
                )
            );
        },
        brand(brand) {
            setRaw(
                produce(
                    (draft) => {
                        // `id` = legacy id (what the old app resolves by),
                        // `brandID` = ObjectId. `||` so an empty legacyID falls
                        // back too. See services/Brands/brandReference.ts.
                        draft.data.brand = {
                            id: brand.legacyID || brand.id,
                            brandID: brand.id,
                        };
                    }
                )
            );
        },
        countries(countries) {
            setRaw(
                produce(
                    (draft) => {
                        draft.data.countries = countries;
                    }
                )
            );
        },
        media(media) {
            setRaw(
                produce(
                    (draft) => {
                        draft.data.media = media;
                    }
                )
            );
        },
        properties(properties) {
            setRaw(
                produce(
                    (draft) => {
                        draft.data.properties = properties;
                    }
                )
            );
        },
        components(components) {
            setRaw(
                produce(
                    (draft) => {
                        draft.data.components = components;
                    }
                )
            );
        },
        layers(layers) {
            setRaw(
                produce(
                    (draft) => {
                        draft.data.layers = layers;
                    }
                )
            );
        },
        materials(materials) {
            setRaw(
                produce(
                    (draft) => {
                        draft.data.materials = materials;
                    }
                )
            );
        },
    };

    return {
        reader,
        writer,
        raw,
    };
}
